'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { LoginScreen } from '@/components/login-screen';
import { AdminDashboard } from '@/components/admin-dashboard';
import { OfficerDashboard } from '@/components/officer-dashboard';
import { VoterSearchScreen } from '@/components/voter-search';
import { VoterVerifyScreen } from '@/components/voter-verify';
import { Report14A } from '@/components/report-14a';
import { Report15A } from '@/components/report-15a';
import { Report17A } from '@/components/report-17a';
import { Report18A } from '@/components/report-18a';
import { SyncStatusPanel } from '@/components/sync-status-panel';
import { offlineDB } from '@/lib/offline-db';

export default function HomePage() {
  const { isAuthenticated, role, restoreSession } = useAuthStore();
  const { currentView, setIsOnline, setCurrentView } = useAppStore();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    restoreSession();
    offlineDB.init();
    queueMicrotask(() => {
      setInitialized(true);
      // Set the correct view based on restored auth state
      const { isAuthenticated, role } = useAuthStore.getState();
      if (isAuthenticated && role) {
        setCurrentView(role === 'admin' ? 'admin-dashboard' : 'officer-dashboard');
      }
    });
  }, [restoreSession, setCurrentView]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setIsOnline]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-amber-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-emerald-800 font-medium">लोड हो रहा है...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppHeader />
      <main className="flex-1 w-full overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          {currentView === 'admin-dashboard' && role === 'admin' && <AdminDashboard />}
          {currentView === 'officer-dashboard' && role === 'officer' && <OfficerDashboard />}
          {currentView === 'voter-search' && <VoterSearchScreen />}
          {currentView === 'voter-verify' && <VoterVerifyScreen />}
          {currentView === 'report-14a' && <Report14A />}
          {currentView === 'report-15a' && <Report15A />}
          {currentView === 'report-17a' && <Report17A />}
          {currentView === 'report-18a' && <Report18A />}
          {currentView === 'sync-status' && <SyncStatusPanel />}
        </div>
      </main>
      <AppFooter />
    </div>
  );
}

function AppHeader() {
  const { role, user, logout } = useAuthStore();
  const { currentView, setCurrentView, isOnline, dataDownloaded } = useAppStore();

  const getBackView = useCallback(() => {
    if (currentView === 'voter-search' || currentView === 'sync-status') return 'officer-dashboard';
    if (currentView === 'voter-verify') return 'voter-search';
    if (currentView === 'report-14a' || currentView === 'report-15a' || currentView === 'report-17a' || currentView === 'report-18a') return 'officer-dashboard';
    return role === 'admin' ? 'admin-dashboard' : 'officer-dashboard';
  }, [currentView, role]);

  const showBack = currentView !== 'admin-dashboard' && currentView !== 'officer-dashboard';

  return (
    <header className="bg-gradient-to-r from-emerald-800 via-emerald-700 to-emerald-800 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {showBack && (
              <button
                onClick={() => setCurrentView(getBackView())}
                className="p-2 hover:bg-emerald-600 rounded-lg transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-amber-300 bg-white">
                <img
                  src="/mpsec-logo.png"
                  alt="म.प्र. राज्य निर्वाचन आयोग"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-bold leading-tight truncate">म.प्र. राज्य निर्वाचन आयोग</h1>
                <p className="text-[10px] sm:text-xs text-emerald-200 truncate">Voter Register - मतदाता रजिस्टर</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {role === 'officer' && (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium ${isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="hidden xs:inline">{isOnline ? 'ऑनलाइन' : 'ऑफलाइन'}</span>
                </span>
                {dataDownloaded && (
                  <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    डाउनलोड
                  </span>
                )}
              </div>
            )}
            <div className="text-right text-xs hidden md:block">
              <p className="font-medium">{user?.name}</p>
              <p className="text-emerald-200">{role === 'admin' ? 'व्यवस्थापक' : 'मतदान अधिकारी'}</p>
            </div>
            <button
              onClick={() => {
                logout();
                setCurrentView(role === 'admin' ? 'admin-dashboard' : 'officer-dashboard');
              }}
              className="p-2 hover:bg-emerald-600 rounded-lg transition-colors"
              title="लॉग आउट"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function AppFooter() {
  return (
    <footer className="bg-emerald-900 text-emerald-200 text-center py-2 px-3 text-[10px] sm:text-xs mt-auto safe-area-bottom">
      <p>© म.प्र. राज्य निर्वाचन आयोग | Voter Register System v1.0</p>
    </footer>
  );
}
